import React, { useEffect, useMemo, useState } from "react";
import {
  EuiBadge,
  EuiButton,
  EuiButtonIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutHeader,
  EuiListGroup,
  EuiListGroupItem,
  EuiPanel,
  EuiSpacer,
  EuiStat,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { useNavigate } from "react-router-dom";
import { OpsanioApplianceData } from "..";
import { useAnketaI18n } from "src/internationalization";
import { AnketaActionsEnum, AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { OpsanioApplianceFlyoutMetadata } from "./metadta";
import { OpsanioApplianceFlyoutWave } from "./wave";
import { GitLabMerge, OpsanioVersions, OpsanioWaves } from "src/questionnaires";
import { Catalog } from "anketa-core/types";
import OpsanioApplianceCostCalculator from "./cost";
import { OpsanioApplianceFlyoutGitLabIssues } from "./gitlab-issues";
import { OpsanioApplianceFlyoutDocumentation } from "./network-documentation";
import { OpsanioApplianceFlyoutPrtg } from "./prtg";
import { OpsanioApplianceFlyoutOtobo } from "./otobo";
import { OpsanioApplianceFlyoutFeedback } from "./feedback";
import { OpsanioApplianceFlyoutHealth } from "./health";
import { OpsanioApplianceFlyoutAuth } from "./auth";
import { OpsanioApplianceFlyoutPangolin } from "./pangolin";
import { OpsanioApplianceFlyoutOidc } from "./oidc";
import { OpsanioApplianceFlyoutHardware } from "./hardware";
import { OpsanioApplianceFlyoutOpl } from "./opl";
import { OpsanioApplianceFlyoutResources } from "./resources";
import { FleetHealthEntry } from "../useFleetHealth";
import { EMPTY_HEALTH, HealthStatus, HEALTH_ICON } from "../health";
import { waveColor } from "../waves";
import { relativeLabel } from "../format";

const STATUS_BADGE: Record<HealthStatus, string> = {
  healthy: "success",
  warning: "warning",
  critical: "danger",
  unknown: "hollow",
};

/** Keys typed into an input must never page the flyout. */
const isEditableTarget = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return (
    el.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) ||
    !!el.closest("[contenteditable='true'], .ouiComboBox, .euiComboBox")
  );
};

type OpsanioApplianceFlyoutTabs =
  | "health"
  | "auth"
  | "hardware"
  | "metadata"
  | "wave"
  | "cost"
  | "issues"
  | "documentation"
  | "prtg"
  | "otobo"
  | "pangolin"
  | "oidc"
  | "feedback"
  | "opl"
  | "resources";

export const OpsanioApplianceFlyout = ({
  onClose,
  appliance,
  healthEntry,
  healthLoading = false,
  position,
  onNavigate,
  loadAllData,
  onWaveTabOpen,
  versions,
  merges,
  tags,
}: {
  onClose: () => void;
  appliance: OpsanioApplianceData;
  /**
   * The page's live-health sweep entry for this appliance: feeds the header's
   * status and the navigation counts without another round trip.
   */
  healthEntry?: FleetHealthEntry;
  /** The first sweep is still running. */
  healthLoading?: boolean;
  /** Where this appliance sits in the table's current order. */
  position?: { index: number; total: number };
  /** Step to the previous (-1) / next (+1) appliance of that order. */
  onNavigate?: (delta: number) => void;
  loadAllData: (updateLoadingState?: boolean) => Promise<void>;
  /** Lets the page load the wave tab's GitLab-backed data on first use. */
  onWaveTabOpen?: () => void;
  versions: Record<OpsanioWaves, OpsanioVersions>;
  /** `null` while not loaded yet - the timeline shows a spinner. */
  merges: Record<OpsanioWaves, GitLabMerge> | null;
  tags: any;
  /** Kept for callers; the questionnaire now opens on its own page. */
  updateCatalog?: (c: Catalog) => void;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const { checkPermission } = useAnketaAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<OpsanioApplianceFlyoutTabs>("health");
  const i18nPrefix = "anketa.opsanio.appliance.flyout";

  useEffect(() => {
    if (tab === "wave") onWaveTabOpen?.();
  }, [tab, onWaveTabOpen]);

  // SSO login stats only make sense for the waves that run real Keycloak SSO.
  const showAuth = appliance.wave === "main" || appliance.wave === "qa";

  // Pangolin endpoints are gated by the dedicated `opsanio/pangolin · view`
  // resource. UI affordance only - the route is the boundary.
  const showPangolin = checkPermission(
    AnketaResourcesEnum.opsanioPangolin,
    AnketaActionsEnum.view,
  );

  // The OPL / Regeltermine tool sits behind its own `opsanio/opl · view` resource,
  // so it can be delegated without widening appliance access. UI affordance
  // only - the routes are the boundary.
  const showOpl = checkPermission(
    AnketaResourcesEnum.opsanioOpl,
    AnketaActionsEnum.view,
  );

  const content = useMemo<JSX.Element>(() => {
    switch (tab) {
      case "health":
        return (
          <OpsanioApplianceFlyoutHealth
            appliance={appliance}
            onOpenMonitoring={() => setTab("prtg")}
          />
        );
      case "auth":
        // Guard: if the appliance is not on a main/qa wave the tab is hidden,
        // but a stale `tab` state from a previous appliance falls back to health.
        return showAuth ? (
          <OpsanioApplianceFlyoutAuth appliance={appliance} />
        ) : (
          <OpsanioApplianceFlyoutHealth
            appliance={appliance}
            onOpenMonitoring={() => setTab("prtg")}
          />
        );
      case "hardware":
        return <OpsanioApplianceFlyoutHardware appliance={appliance} />;
      case "metadata":
        return <OpsanioApplianceFlyoutMetadata appliance={appliance} />;
      case "wave":
        return (
          <OpsanioApplianceFlyoutWave
            appliance={appliance}
            loadAllData={loadAllData}
            merges={merges}
            tags={tags}
            versions={versions}
          />
        );
      case "cost":
        return <OpsanioApplianceCostCalculator appliance={appliance} />;
      case "issues":
        return <OpsanioApplianceFlyoutGitLabIssues appliance={appliance} />;
      case "resources":
        return (
          <OpsanioApplianceFlyoutResources
            appliance={appliance}
            onOpenPangolin={
              showPangolin ? () => setTab("pangolin") : undefined
            }
            onOpenHardware={() => setTab("hardware")}
          />
        );
      case "documentation":
        return <OpsanioApplianceFlyoutDocumentation appliance={appliance} />;
      case "prtg":
        return <OpsanioApplianceFlyoutPrtg appliance={appliance} />;
      case "otobo":
        return <OpsanioApplianceFlyoutOtobo appliance={appliance} />;
      case "pangolin":
        // Guard: hidden without `opsanio/pangolin · view`; a stale tab state from
        // a previous appliance falls back to health.
        return showPangolin ? (
          <OpsanioApplianceFlyoutPangolin appliance={appliance} />
        ) : (
          <OpsanioApplianceFlyoutHealth
            appliance={appliance}
            onOpenMonitoring={() => setTab("prtg")}
          />
        );
      case "oidc":
        return <OpsanioApplianceFlyoutOidc appliance={appliance} />;
      case "feedback":
        return <OpsanioApplianceFlyoutFeedback appliance={appliance} />;
      case "opl":
        // Guard: hidden without `opsanio/opl · view`; a stale tab state from a
        // previous appliance falls back to health.
        return showOpl ? (
          <OpsanioApplianceFlyoutOpl appliance={appliance} />
        ) : (
          <OpsanioApplianceFlyoutHealth
            appliance={appliance}
            onOpenMonitoring={() => setTab("prtg")}
          />
        );
      default:
        <></>;
    }
    // The wave tab's data (versions/merges/tags) arrives after the tab opens,
    // so it has to invalidate the memo too.
  }, [appliance, tab, versions, merges, tags]);

  useEffect(() => {
    if (content === null) {
    }
  }, [appliance]);

  // Keyboard paging: ← / → step through the table's current order.
  useEffect(() => {
    if (!onNavigate) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (isEditableTarget(e.target)) return;
      if (e.key === "ArrowLeft") onNavigate(-1);
      if (e.key === "ArrowRight") onNavigate(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onNavigate]);

  const health = healthEntry?.health ?? EMPTY_HEALTH;
  const engagement = healthEntry?.engagement;

  const statusLabel: Record<HealthStatus, string> = {
    healthy: translate("anketa.opsanio.overview.status.healthy", {
      defaultMessage: "Healthy",
    }),
    warning: translate("anketa.opsanio.overview.status.warning", {
      defaultMessage: "Warning",
    }),
    critical: translate("anketa.opsanio.overview.status.critical", {
      defaultMessage: "Critical",
    }),
    unknown: translate("anketa.opsanio.overview.status.unmonitored", {
      defaultMessage: "Unmonitored",
    }),
  };

  type NavItem = {
    key: OpsanioApplianceFlyoutTabs;
    label: string;
    /** Open items worth a badge (0 / undefined = none shown). */
    count?: number;
    urgent?: boolean;
  };

  /**
   * One navigation rail instead of two tab rows: the views grouped by the
   * question they answer, with live counts where there is something open.
   * The permission gates are unchanged (Auth only on main/qa, Pangolin and
   * OPL only with their resources). The questionnaire is no longer embedded -
   * the header's "Open questionnaire" button opens its own page.
   */
  const navGroups = useMemo(
    () =>
      [
        {
          key: "overview",
          label: translate(`${i18nPrefix}.group.overview`, {
            defaultMessage: "Overview",
          }),
          items: [
            {
              key: "health",
              label: translate(`${i18nPrefix}.health.tab`, {
                defaultMessage: "Health",
              }),
            },
            {
              key: "hardware",
              label: translate(`${i18nPrefix}.hardware.tab`, {
                defaultMessage: "Hardware",
              }),
            },
            {
              key: "prtg",
              label: translate(`${i18nPrefix}.prtg.tab`, {
                defaultMessage: "Monitoring (PRTG)",
              }),
              count: health.monitored ? health.activeIncidents : undefined,
              urgent: true,
            },
          ] as NavItem[],
        },
        {
          key: "support",
          label: translate(`${i18nPrefix}.group.support`, {
            defaultMessage: "Support",
          }),
          items: [
            {
              key: "otobo",
              label: translate(`${i18nPrefix}.otobo.tab`, {
                defaultMessage: "Tickets (OTOBO)",
              }),
              count: engagement?.otoboOpen,
            },
            {
              key: "issues",
              label: translate(`${i18nPrefix}.issues.tab`, {
                defaultMessage: "GitLab issues",
              }),
              count: engagement?.gitlabOpen,
            },
            {
              key: "feedback",
              label: translate(`${i18nPrefix}.feedback.tab`, {
                defaultMessage: "Feedback",
              }),
              count: engagement?.feedback,
            },
            // Sits with the two systems it promotes into (OTOBO, GitLab).
            ...(showOpl
              ? [
                  {
                    key: "opl",
                    label: translate(`${i18nPrefix}.opl.tab`, {
                      defaultMessage: "OPL / Regeltermine",
                    }),
                  },
                ]
              : []),
          ] as NavItem[],
        },
        {
          key: "access",
          label: translate(`${i18nPrefix}.group.access`, {
            defaultMessage: "Access",
          }),
          items: [
            ...(showAuth
              ? [
                  {
                    key: "auth",
                    label: translate(`${i18nPrefix}.auth.tab`, {
                      defaultMessage: "Sign-ins",
                    }),
                  },
                ]
              : []),
            {
              key: "oidc",
              label: translate(`${i18nPrefix}.oidc.tab`, {
                defaultMessage: "OIDC clients",
              }),
            },
            ...(showPangolin
              ? [
                  {
                    key: "pangolin",
                    label: translate(`${i18nPrefix}.pangolin.tab`, {
                      defaultMessage: "Pangolin",
                    }),
                  },
                ]
              : []),
          ] as NavItem[],
        },
        {
          key: "admin",
          label: translate(`${i18nPrefix}.group.admin`, {
            defaultMessage: "Administration",
          }),
          items: [
            {
              key: "resources",
              label: translate(`${i18nPrefix}.resources.tab`, {
                defaultMessage: "Resources",
              }),
            },
            {
              key: "wave",
              label: translate(`${i18nPrefix}.wave.tab`, {
                defaultMessage: "Wave",
              }),
            },
            {
              key: "metadata",
              label: translate("anketa.documentation.opsanio.project.metadata"),
            },
            {
              key: "cost",
              label: translate("anketa.opsanio.appliance.cost"),
            },
            {
              key: "documentation",
              label: translate(`${i18nPrefix}.documentation.tab`, {
                defaultMessage: "Documentation",
              }),
            },
          ] as NavItem[],
        },
      ].filter((group) => group.items.length > 0),
    [translate, showAuth, showPangolin, showOpl, health, engagement],
  );

  // --- Header ---------------------------------------------------------------
  const meta = appliance.common?.metadata;
  const loc = appliance.common?.location;
  const counts = appliance.common?.counts;
  const contacts = appliance.common?.general?.contacts;
  const metaLine = [
    meta?.name,
    [loc?.city, loc?.country].filter(Boolean).join(", "),
    counts
      ? translate(`${i18nPrefix}.header.counts`, {
          values: { agents: counts.agents ?? 0, server: counts.server ?? 0 },
          defaultMessage: "{{agents}} agents · {{server}} server",
        })
      : "",
    contacts?.sales?.length
      ? translate(`${i18nPrefix}.header.sales`, {
          values: { names: contacts.sales.join(", ") },
          defaultMessage: "Sales {{names}}",
        })
      : "",
    contacts?.technical?.length
      ? translate(`${i18nPrefix}.header.technical`, {
          values: { names: contacts.technical.join(", ") },
          defaultMessage: "Technical {{names}}",
        })
      : "",
  ]
    .filter(Boolean)
    .join(" · ");

  const canPage = !!onNavigate && !!position && position.index >= 0;

  const healthStrip = !healthEntry ? (
    <EuiText size="s" color="subdued">
      {healthLoading
        ? translate(`${i18nPrefix}.header.health.loading`, {
            defaultMessage: "Loading live health…",
          })
        : translate(`${i18nPrefix}.header.health.none`, {
            defaultMessage: "No live health for this appliance.",
          })}
    </EuiText>
  ) : !health.monitored ? (
    <EuiText size="s" color="subdued">
      {translate(`${i18nPrefix}.header.health.unmonitored`, {
        defaultMessage: "Not monitored - no PRTG sensors are reporting.",
      })}
    </EuiText>
  ) : (
    <EuiPanel hasBorder paddingSize="s" hasShadow={false}>
      <EuiFlexGroup gutterSize="l" responsive={false} wrap>
        <EuiFlexItem>
          <EuiStat
            titleSize="xs"
            title={health.score !== null ? `${health.score} %` : "-"}
            description={translate(`${i18nPrefix}.header.score`, {
              defaultMessage: "Health score",
            })}
            reverse
          />
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiStat
            titleSize="xs"
            title={`${health.counts?.up ?? 0} / ${health.counts?.total ?? 0}`}
            description={translate(`${i18nPrefix}.header.sensors`, {
              defaultMessage: "Sensors up",
            })}
            reverse
          />
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiStat
            titleSize="xs"
            title={`${health.activeIncidents}`}
            titleColor={health.activeIncidents > 0 ? "danger" : "default"}
            description={translate(`${i18nPrefix}.header.incidents`, {
              defaultMessage: "Active incidents",
            })}
            reverse
          />
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiStat
            titleSize="xs"
            title={relativeLabel(health.lastSync, translate)}
            description={translate(`${i18nPrefix}.header.lastCheck`, {
              defaultMessage: "Last check-in",
            })}
            reverse
          />
        </EuiFlexItem>
      </EuiFlexGroup>
    </EuiPanel>
  );

  return (
    <EuiFlyout
      onClose={onClose}
      ownFocus={true}
      size="l"
      aria-labelledby="applianceFlyoutTitle"
    >
      <EuiFlyoutHeader hasBorder>
        {/* Right padding keeps clear of the flyout's own close button. */}
        <EuiFlexGroup
          alignItems="flexStart"
          gutterSize="m"
          responsive={false}
          style={{ paddingRight: 32 }}
        >
          <EuiFlexItem style={{ minWidth: 0 }}>
            <EuiFlexGroup
              gutterSize="s"
              alignItems="center"
              responsive={false}
              wrap
            >
              <EuiFlexItem grow={false}>
                <EuiTitle size="s">
                  <h2 id="applianceFlyoutTitle">
                    {appliance.displayName || appliance.id}
                  </h2>
                </EuiTitle>
              </EuiFlexItem>
              {appliance.wave && (
                <EuiFlexItem grow={false}>
                  <EuiBadge color={waveColor(appliance.wave)}>
                    {appliance.wave}
                  </EuiBadge>
                </EuiFlexItem>
              )}
              <EuiFlexItem grow={false}>
                <EuiBadge
                  color={STATUS_BADGE[health.status]}
                  iconType={HEALTH_ICON[health.status]}
                >
                  {statusLabel[health.status]}
                </EuiBadge>
              </EuiFlexItem>
              {appliance.archived && (
                <EuiFlexItem grow={false}>
                  <EuiBadge color="hollow" iconType="folderClosed">
                    {translate("anketa.opsanio.table.badge.archived", {
                      defaultMessage: "Archived",
                    })}
                  </EuiBadge>
                </EuiFlexItem>
              )}
            </EuiFlexGroup>
            {metaLine && (
              <EuiText size="s" color="subdued">
                {metaLine}
              </EuiText>
            )}
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
              <EuiFlexItem grow={false}>
                <EuiButton
                  size="s"
                  iconType="popout"
                  iconSide="right"
                  onClick={() =>
                    navigate(
                      `/${AnketaResourcesEnum.questionnaires}/${appliance.catalogId}/${appliance.id}`,
                    )
                  }
                >
                  {translate(`${i18nPrefix}.header.openQuestionnaire`, {
                    defaultMessage: "Open questionnaire",
                  })}
                </EuiButton>
              </EuiFlexItem>
              {canPage && (
                <>
                  <EuiFlexItem grow={false}>
                    <EuiButtonIcon
                      iconType="arrowLeft"
                      aria-label={translate(`${i18nPrefix}.header.previous`, {
                        defaultMessage: "Previous appliance",
                      })}
                      isDisabled={position!.index <= 0}
                      onClick={() => onNavigate!(-1)}
                    />
                  </EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    <EuiText size="xs" color="subdued">
                      {position!.index + 1} / {position!.total}
                    </EuiText>
                  </EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    <EuiButtonIcon
                      iconType="arrowRight"
                      aria-label={translate(`${i18nPrefix}.header.next`, {
                        defaultMessage: "Next appliance",
                      })}
                      isDisabled={position!.index >= position!.total - 1}
                      onClick={() => onNavigate!(1)}
                    />
                  </EuiFlexItem>
                </>
              )}
            </EuiFlexGroup>
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer size="s" />
        {healthStrip}
      </EuiFlyoutHeader>
      <EuiFlyoutBody>
        <EuiFlexGroup gutterSize="l" responsive={false} alignItems="flexStart">
          <EuiFlexItem
            grow={false}
            style={{ width: 200, position: "sticky", top: 0 }}
          >
            <nav
              aria-label={translate(`${i18nPrefix}.nav.aria`, {
                defaultMessage: "Appliance sections",
              })}
            >
              {navGroups.map((group, gi) => (
                <div key={group.key}>
                  {gi > 0 && <EuiSpacer size="m" />}
                  <EuiText size="xs" color="subdued">
                    <strong style={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                      {group.label}
                    </strong>
                  </EuiText>
                  <EuiSpacer size="xs" />
                  <EuiListGroup flush gutterSize="none" maxWidth={false}>
                    {group.items.map((item) => (
                      <EuiListGroupItem
                        key={item.key}
                        size="s"
                        isActive={tab === item.key}
                        onClick={() => setTab(item.key)}
                        aria-current={tab === item.key ? "page" : undefined}
                        label={
                          <EuiFlexGroup
                            gutterSize="s"
                            alignItems="center"
                            responsive={false}
                          >
                            <EuiFlexItem>{item.label}</EuiFlexItem>
                            {!!item.count && item.count > 0 && (
                              <EuiFlexItem grow={false}>
                                <EuiBadge
                                  color={item.urgent ? "danger" : "hollow"}
                                >
                                  {item.count}
                                </EuiBadge>
                              </EuiFlexItem>
                            )}
                          </EuiFlexGroup>
                        }
                      />
                    ))}
                  </EuiListGroup>
                </div>
              ))}
            </nav>
          </EuiFlexItem>
          <EuiFlexItem style={{ minWidth: 0 }}>{content}</EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutBody>
    </EuiFlyout>
  );
};
